import React, { useState } from 'react';
import { 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  Typography, 
  Paper, 
  Box, 
  Button, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  IconButton,
  InputAdornment,
  Collapse,
  alpha,
  Chip,
  Tooltip,
  MenuItem
} from '@mui/material';
import { 
  Description as DescriptionIcon, 
  Add as AddIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
  LibraryBooks as LibraryBooksIcon,
  Category as CategoryIcon
} from '@mui/icons-material';

// Common styles for scrollbars
const commonStyles = {
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: '8px',
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme => alpha(theme.palette.primary.main, 0.2),
      borderRadius: '20px',
      border: '2px solid transparent',
      backgroundClip: 'padding-box',
      transition: 'background 0.2s ease',
      '&:hover': {
        background: theme => alpha(theme.palette.primary.main, 0.3),
      }
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
      border: 'none',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: theme => `${alpha(theme.palette.primary.main, 0.2)} transparent`,
    '&:hover': {
      '&::-webkit-scrollbar-thumb': {
        background: theme => alpha(theme.palette.primary.main, 0.3),
      }
    }
  },
};

const DocumentationSidebar = ({ pages, selectedPageId, onSelectPage, onAddPage, loading }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageError, setNewPageError] = useState('');
  const [newPageCategory, setNewPageCategory] = useState('Uncategorized');
  const [categoriesExpanded, setCategoriesExpanded] = useState({});
  const newCategoryInputRef = React.useRef(null);

  // Group pages by category
  const pagesByCategory = pages.reduce((acc, page) => {
    const category = page.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(page);
    return acc;
  }, {});

  // Get unique categories
  const availableCategories = Object.keys(pagesByCategory).sort();

  const toggleCategory = (category) => {
    setCategoriesExpanded({
      ...categoriesExpanded,
      [category]: !categoriesExpanded[category]
    });
  };

  const filteredCategories = searchTerm.trim() 
    ? Object.keys(pagesByCategory).filter(category => 
        category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pagesByCategory[category].some(page => 
          page.title.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : Object.keys(pagesByCategory);

  const handleAddPage = () => {
    if (!newPageTitle.trim()) {
      setNewPageError('Title is required');
      return;
    }
    
    // Check if the title already exists
    const titleExists = pages.some(page => page.title.toLowerCase() === newPageTitle.toLowerCase());
    if (titleExists) {
      setNewPageError('A page with this title already exists');
      return;
    }
    
    // Pass an object with the title property
    onAddPage({
      title: newPageTitle.trim(),
      category: newPageCategory
    });
    setIsAddDialogOpen(false);
    setNewPageTitle('');
    setNewPageError('');
  };

  const handleOpenAddDialog = () => {
    setIsAddDialogOpen(true);
    setNewPageTitle('');
    setNewPageError('');
    setNewPageCategory('Uncategorized');
  };

  const handleCategoryChange = (e) => {
    setNewPageCategory(e.target.value);
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden'
      }}
    >
      <Box 
        sx={{ 
          p: 2.5, 
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: theme => `linear-gradient(90deg, 
              ${alpha(theme.palette.primary.main, 0)}, 
              ${alpha(theme.palette.primary.main, 0.2)}, 
              ${alpha(theme.palette.secondary.main, 0.2)}, 
              ${alpha(theme.palette.secondary.main, 0)}
            )`,
            opacity: 0.6
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Box 
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <LibraryBooksIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography 
            variant="h6" 
            component="h2" 
            sx={{ 
              fontFamily: 'StrawberrySansDisplay',
              fontWeight: 500,
              fontSize: '1.1rem',
              lineHeight: 1.2,
              color: 'text.primary'
            }}
          >
            Documentation
          </Typography>
        </Box>
        
        <TextField
          fullWidth
          size="small"
          placeholder="Search pages..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton 
                  size="small" 
                  onClick={() => setSearchTerm('')}
                  sx={{ color: 'text.secondary' }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme => alpha(theme.palette.divider, 0.8)
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main'
              }
            }
          }}
          sx={{ mb: 2 }}
        />
        
        <Button 
          variant="contained" 
          color="primary" 
          fullWidth
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
          sx={{
            borderRadius: 2,
            py: 1,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4
            }
          }}
        >
          New Document
        </Button>
      </Box>

      <Typography
        variant="subtitle2"
        sx={{
          px: 2.5,
          py: 1.5,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.75rem',
          fontWeight: 500,
          fontFamily: 'StrawberrySansDisplay',
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <CategoryIcon sx={{ fontSize: 16 }} />
        Categories
      </Typography>

      <Box 
        sx={{ 
          overflow: 'auto', 
          flexGrow: 1,
          ...commonStyles.scrollbar
        }}
      >
        {filteredCategories.length > 0 ? (
          filteredCategories.map(category => {
            const categoryPages = searchTerm.trim()
              ? pagesByCategory[category].filter(page => 
                  page.title.toLowerCase().includes(searchTerm.toLowerCase())
                )
              : pagesByCategory[category];
              
            // If we're searching and no pages match in this category, don't show the category
            if (searchTerm.trim() && categoryPages.length === 0) {
              return null;
            }
            
            const isExpanded = categoriesExpanded[category] !== false; // Default to expanded
            
            return (
              <Box key={category} sx={{ mb: 1 }}>
                <ListItem 
                  button 
                  onClick={() => toggleCategory(category)}
                  sx={{ 
                    px: 2.5,
                    py: 1,
                    '&:hover': {
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemIcon>
                  <ListItemText 
                    primary={category} 
                    primaryTypographyProps={{ 
                      fontWeight: 500, 
                      fontSize: '0.925rem'
                    }}
                  />
                  <Chip
                    label={categoryPages.length}
                    size="small"
                    sx={{
                      height: 20,
                      minWidth: 20,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                      color: 'primary.main'
                    }}
                  />
                </ListItem>
                
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List 
                    component="div" 
                    disablePadding
                    sx={{ pb: 1 }}
                  >
                    {categoryPages
                      .sort((a, b) => a.title.localeCompare(b.title))
                      .map(page => (
                        <ListItem
                          button
                          key={page.id}
                          selected={selectedPageId === page.id}
                          onClick={() => onSelectPage(page.id)}
                          sx={{ 
                            pl: 6.5,
                            pr: 2.5,
                            py: 0.75,
                            minHeight: 38,
                            position: 'relative',
                            '&.Mui-selected': {
                              bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 4,
                                bottom: 4,
                                width: 3,
                                borderRadius: '0 4px 4px 0',
                                bgcolor: 'primary.main'
                              },
                              '&:hover': {
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.12)
                              }
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 28, color: 'text.secondary' }}>
                            <DescriptionIcon fontSize="small" />
                          </ListItemIcon>
                          <Tooltip title={page.title} placement="right">
                            <ListItemText 
                              primary={page.title}
                              primaryTypographyProps={{ 
                                fontSize: '0.875rem',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap'
                              }}
                            />
                          </Tooltip>
                        </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          })
        ) : (
          <Box 
            sx={{ 
              p: 3, 
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <DescriptionIcon color="disabled" sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {loading ? 'Loading pages...' : 'No pages found'}
            </Typography>
            {searchTerm && (
              <Button 
                size="small" 
                variant="outlined"
                onClick={() => setSearchTerm('')}
                startIcon={<ClearIcon fontSize="small" />}
              >
                Clear Search
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Dialog 
        open={isAddDialogOpen} 
        onClose={() => setIsAddDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme => `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            pb: 1,
            fontFamily: 'StrawberrySansDisplay'
          }}
        >
          Create New Document
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2, pb: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Page Title"
            fullWidth
            variant="outlined"
            value={newPageTitle}
            onChange={(e) => setNewPageTitle(e.target.value)}
            error={!!newPageError}
            helperText={newPageError}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddPage();
              }
            }}
            InputProps={{
              sx: {
                borderRadius: 2
              }
            }}
            sx={{ mb: 2 }}
          />
          
          <TextField
            select
            fullWidth
            label="Category"
            value={newPageCategory}
            onChange={handleCategoryChange}
            variant="outlined"
            InputProps={{
              sx: {
                borderRadius: 2
              }
            }}
            sx={{ mb: 2 }}
          >
            {availableCategories.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
            {!availableCategories.includes(newPageCategory) && newPageCategory !== '' && (
              <MenuItem value={newPageCategory}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <span>"{newPageCategory}"</span>
                  <Chip 
                    label="New" 
                    size="small" 
                    color="primary" 
                    sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} 
                  />
                </Box>
              </MenuItem>
            )}
          </TextField>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Create a new category
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Enter new category name..."
                variant="outlined"
                inputRef={newCategoryInputRef}
                InputProps={{
                  sx: {
                    borderRadius: 2
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    setNewPageCategory(e.target.value.trim());
                    e.target.value = '';
                  }
                }}
              />
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => {
                  if (newCategoryInputRef.current && newCategoryInputRef.current.value.trim()) {
                    setNewPageCategory(newCategoryInputRef.current.value.trim());
                    newCategoryInputRef.current.value = '';
                  }
                }}
                sx={{ flexShrink: 0, borderRadius: 2 }}
              >
                Add
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setIsAddDialogOpen(false)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddPage} 
            color="primary" 
            variant="contained" 
            disabled={!newPageTitle.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DocumentationSidebar; 